<script setup>
import {ref, reactive, onMounted, watch, useTemplateRef, computed, onUnmounted} from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { useCommonComposable } from '@/composables/commonComposable.js';
import MiniCart from '@/components/App/mini-cart.vue'
import SearchOverlay from '@/components/App/search-overlay.vue'
import KeyboardShortcut from "@/components/App/KeyboardShortcut.vue";

const store = useStore()
const route = useRoute()
const router = useRouter()
const { isMobile } = useCommonComposable();

const authContainer = useTemplateRef('cart-svg');

// Данные компонента
const isShowCart = ref(false)
const isShowSearch = ref(false)

// Вычисляемые свойства
const cart = computed(() => store.state.cart)

const getCartIconHeight = computed(() => getComputedStyle(authContainer.value).height)

// Методы
const showCart = () => {
    isShowCart.value = !isShowCart.value
}

const closeCart = () => {
    isShowCart.value = false
}

const showSearch = () => {
    isShowSearch.value = !isShowSearch.value
}

const closeSearch = () => {
    isShowSearch.value = false
}

const handleKeydown = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        showSearch();
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'u') {
        event.preventDefault();
        router.push({ path: '/profile' });
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'l') {
        event.preventDefault();
        router.push({ path: '/products' });
    }
};

// Обработчик изменения маршрута (watch)
onMounted(() => {
    watch(
        () => route.query.searchText,
        (newValue, oldValue) => {
            closeSearch()
        }
    )
    window.addEventListener('keydown', handleKeydown);
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
});

// Компоненты
defineExpose({
    showCart,
    closeCart,
    showSearch,
    closeSearch,
    cart
})
</script>

<template>
    <header class="header main-page-header" v-if="route.path==='/'">
        <div class="aside"></div>
        <div class="logo" @click="router.push('/')">
            <svg width="36" height="29" viewBox="0 0 36 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path id="letter" d="M23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723L23.4245 9.03549ZM14.1593 18.0934L18.7197 23.0604L27.5302 13.6505L21.6307 12.741L14.1593 18.0934ZM5.60399 8.77538L11.478 15.1729L19.5076 9.4206L19.5068 9.41122L18.9961 4.08678H9.10207L5.60399 8.77538Z" fill="#D9D9D9"/>
                <path d="M22.5723 0.151169L23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723ZM1.11248 9.00413L18.695 28.1541L34.5435 11.2273L22.9894 9.44627L22.1432 0.623442H7.36487L1.11248 9.00413Z" fill="#D4D4D4"/>
            </svg>
        </div>
        <div class="aside">
            <div class="sign-outer glass-button">
                <router-link to="/profile" class="sign">
                    <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.35 15.1C5.2 14.45 6.15 13.9377 7.2 13.563C8.25 13.1883 9.35 13.0007 10.5 13C11.65 13 12.75 13.1877 13.8 13.563C14.85 13.9383 15.8 14.4507 16.65 15.1C17.2333 14.4167 17.6877 13.6417 18.013 12.775C18.3383 11.9083 18.5007 10.9833 18.5 10C18.5 7.78333 17.721 5.89567 16.163 4.337C14.605 2.77833 12.7173 1.99933 10.5 2C8.28333 2 6.39567 2.77933 4.837 4.338C3.27833 5.89667 2.49933 7.784 2.5 10C2.5 10.9833 2.66267 11.9083 2.988 12.775C3.31333 13.6417 3.76733 14.4167 4.35 15.1ZM10.5 11C9.51667 11 8.68733 10.6627 8.012 9.988C7.33667 9.31333 6.99933 8.484 7 7.5C7 6.51667 7.33767 5.68733 8.013 5.012C8.68833 4.33667 9.51733 3.99933 10.5 4C11.4833 4 12.3127 4.33767 12.988 5.013C13.6633 5.68833 14.0007 6.51733 14 7.5C14 8.48333 13.6627 9.31267 12.988 9.988C12.3133 10.6633 11.484 11.0007 10.5 11ZM10.5 20C9.11667 20 7.81667 19.7373 6.6 19.212C5.38333 18.6867 4.325 17.9743 3.425 17.075C2.525 16.175 1.81267 15.1167 1.288 13.9C0.763333 12.6833 0.500667 11.3833 0.5 10C0.5 8.61667 0.762667 7.31667 1.288 6.1C1.81333 4.88333 2.52567 3.825 3.425 2.925C4.325 2.025 5.38333 1.31267 6.6 0.788C7.81667 0.263333 9.11667 0.000666667 10.5 0C11.8833 0 13.1833 0.262667 14.4 0.788C15.6167 1.31333 16.675 2.02567 17.575 2.925C18.475 3.825 19.1877 4.88333 19.713 6.1C20.2383 7.31667 20.5007 8.61667 20.5 10C20.5 11.3833 20.2373 12.6833 19.712 13.9C19.1867 15.1167 18.4743 16.175 17.575 17.075C16.675 17.975 15.6167 18.6877 14.4 19.213C13.1833 19.7383 11.8833 20.0007 10.5 20ZM10.5 18C11.3833 18 12.2167 17.871 13 17.613C13.7833 17.355 14.5 16.984 15.15 16.5C14.5 16.0167 13.7833 15.646 13 15.388C12.2167 15.13 11.3833 15.0007 10.5 15C9.61667 15 8.78333 15.1293 8 15.388C7.21667 15.6467 6.5 16.0173 5.85 16.5C6.5 16.9833 7.21667 17.3543 8 17.613C8.78333 17.8717 9.61667 18.0007 10.5 18ZM10.5 9C10.9333 9 11.2917 8.85833 11.575 8.575C11.8583 8.29167 12 7.93333 12 7.5C12 7.06667 11.8583 6.70833 11.575 6.425C11.2917 6.14167 10.9333 6 10.5 6C10.0667 6 9.70833 6.14167 9.425 6.425C9.14167 6.70833 9 7.06667 9 7.5C9 7.93333 9.14167 8.29167 9.425 8.575C9.70833 8.85833 10.0667 9 10.5 9Z" fill="#1C1C1C"/>
                    </svg>
                    <span>Войти</span>
                </router-link>
            </div>
        </div>
    </header>

    <header :class="['header']" ref="header" v-else-if="!isMobile">
        <div class="container">
            <div class="row">
                <div class="col-12">
                    <div class="content">
                    <div class="left">
                        <div class="logo" @click="router.push('/')">
                            <svg width="36" height="29" viewBox="0 0 36 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path id="letter" d="M23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723L23.4245 9.03549ZM14.1593 18.0934L18.7197 23.0604L27.5302 13.6505L21.6307 12.741L14.1593 18.0934ZM5.60399 8.77538L11.478 15.1729L19.5076 9.4206L19.5068 9.41122L18.9961 4.08678H9.10207L5.60399 8.77538Z" fill="black"/>
                                <path d="M22.5723 0.151169L23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723ZM1.11248 9.00413L18.695 28.1541L34.5435 11.2273L22.9894 9.44627L22.1432 0.623442H7.36487L1.11248 9.00413Z" fill="#D4D4D4"/>
                            </svg>
                        </div>
                        <nav class="menu">
                            <router-link :to="{path: '/products'}"><span>Каталог</span></router-link>
                        </nav>
                    </div>
                    <div class="right nav-icons">
                        <div class="search icn">
                            <svg @click="showSearch" width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.3738 15.46L20.6568 19.742L19.2418 21.157L14.9598 16.874C13.3665 18.1512 11.3848 18.8459 9.34277 18.843C4.37477 18.843 0.342773 14.811 0.342773 9.84302C0.342773 4.87502 4.37477 0.843018 9.34277 0.843018C14.3108 0.843018 18.3428 4.87502 18.3428 9.84302C18.3457 11.885 17.651 13.8668 16.3738 15.46ZM14.3678 14.718C15.6369 13.4129 16.3456 11.6634 16.3428 9.84302C16.3428 5.97602 13.2098 2.84302 9.34277 2.84302C5.47577 2.84302 2.34277 5.97602 2.34277 9.84302C2.34277 13.71 5.47577 16.843 9.34277 16.843C11.1632 16.8459 12.9127 16.1371 14.2178 14.868L14.3678 14.718Z" fill="#1C1C1C"/>
                            </svg>
                            <keyboard-shortcut :description="''" :keys="['Ctrl K']" :is-mini="true" :is-typing="true" :position="'relative'"></keyboard-shortcut>
                            <search-overlay v-if="isShowSearch" @close-search="closeSearch"></search-overlay>
                        </div>
                        <div class="profile icn">
                            <router-link to="/profile">
                                <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.35 15.1C5.2 14.45 6.15 13.9377 7.2 13.563C8.25 13.1883 9.35 13.0007 10.5 13C11.65 13 12.75 13.1877 13.8 13.563C14.85 13.9383 15.8 14.4507 16.65 15.1C17.2333 14.4167 17.6877 13.6417 18.013 12.775C18.3383 11.9083 18.5007 10.9833 18.5 10C18.5 7.78333 17.721 5.89567 16.163 4.337C14.605 2.77833 12.7173 1.99933 10.5 2C8.28333 2 6.39567 2.77933 4.837 4.338C3.27833 5.89667 2.49933 7.784 2.5 10C2.5 10.9833 2.66267 11.9083 2.988 12.775C3.31333 13.6417 3.76733 14.4167 4.35 15.1ZM10.5 11C9.51667 11 8.68733 10.6627 8.012 9.988C7.33667 9.31333 6.99933 8.484 7 7.5C7 6.51667 7.33767 5.68733 8.013 5.012C8.68833 4.33667 9.51733 3.99933 10.5 4C11.4833 4 12.3127 4.33767 12.988 5.013C13.6633 5.68833 14.0007 6.51733 14 7.5C14 8.48333 13.6627 9.31267 12.988 9.988C12.3133 10.6633 11.484 11.0007 10.5 11ZM10.5 20C9.11667 20 7.81667 19.7373 6.6 19.212C5.38333 18.6867 4.325 17.9743 3.425 17.075C2.525 16.175 1.81267 15.1167 1.288 13.9C0.763333 12.6833 0.500667 11.3833 0.5 10C0.5 8.61667 0.762667 7.31667 1.288 6.1C1.81333 4.88333 2.52567 3.825 3.425 2.925C4.325 2.025 5.38333 1.31267 6.6 0.788C7.81667 0.263333 9.11667 0.000666667 10.5 0C11.8833 0 13.1833 0.262667 14.4 0.788C15.6167 1.31333 16.675 2.02567 17.575 2.925C18.475 3.825 19.1877 4.88333 19.713 6.1C20.2383 7.31667 20.5007 8.61667 20.5 10C20.5 11.3833 20.2373 12.6833 19.712 13.9C19.1867 15.1167 18.4743 16.175 17.575 17.075C16.675 17.975 15.6167 18.6877 14.4 19.213C13.1833 19.7383 11.8833 20.0007 10.5 20ZM10.5 18C11.3833 18 12.2167 17.871 13 17.613C13.7833 17.355 14.5 16.984 15.15 16.5C14.5 16.0167 13.7833 15.646 13 15.388C12.2167 15.13 11.3833 15.0007 10.5 15C9.61667 15 8.78333 15.1293 8 15.388C7.21667 15.6467 6.5 16.0173 5.85 16.5C6.5 16.9833 7.21667 17.3543 8 17.613C8.78333 17.8717 9.61667 18.0007 10.5 18ZM10.5 9C10.9333 9 11.2917 8.85833 11.575 8.575C11.8583 8.29167 12 7.93333 12 7.5C12 7.06667 11.8583 6.70833 11.575 6.425C11.2917 6.14167 10.9333 6 10.5 6C10.0667 6 9.70833 6.14167 9.425 6.425C9.14167 6.70833 9 7.06667 9 7.5C9 7.93333 9.14167 8.29167 9.425 8.575C9.70833 8.85833 10.0667 9 10.5 9Z" fill="#1C1C1C"/>
                                </svg>
                            </router-link>
                            <keyboard-shortcut :description="''" :keys="['Ctrl U']" :is-mini="true" :is-typing="true" :position="'relative'"></keyboard-shortcut>
                        </div>
                        <div class="cart icn">
                            <svg @click="showCart" ref="cart-svg" width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19.674 5.19531H1.3252L3.32329 20.1321H17.6735L19.674 5.19531Z" stroke="#1C1C1C" stroke-width="2"/>
                                <path d="M5.16988 5.74221L4.65625 1.86768H15.926L15.679 5.74221" stroke="#1C1C1C" stroke-width="2"/>
                            </svg>
                            <div @click="showCart" ref="cart-svg-notif" class="cart-notification" v-if="cart.totalQuantity > 0">
                                <p>{{ cart.totalQuantity }}</p>
                            </div>
                            <mini-cart v-if="isShowCart" :icn-height="getCartIconHeight" :cart="cart" @close-cart="closeCart()" @mouseleave="isShowCart = false"></mini-cart>
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </div>
    </header>

    <header class="mobile-header" v-else-if="isMobile">
        <div class="logo" @click="router.push('/')">
            <svg width="36" height="29" viewBox="0 0 36 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path id="letter" d="M23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723L23.4245 9.03549ZM14.1593 18.0934L18.7197 23.0604L27.5302 13.6505L21.6307 12.741L14.1593 18.0934ZM5.60399 8.77538L11.478 15.1729L19.5076 9.4206L19.5068 9.41122L18.9961 4.08678H9.10207L5.60399 8.77538Z" fill="black"/>
                <path d="M22.5723 0.151169L23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723ZM1.11248 9.00413L18.695 28.1541L34.5435 11.2273L22.9894 9.44627L22.1432 0.623442H7.36487L1.11248 9.00413Z" fill="#D4D4D4"/>
            </svg>
        </div>
        <span>/</span>
        <span>Baller</span>
    </header>
</template>

<style scoped lang="scss" src="./style.scss">
</style>
